import { startTransition, useCallback, useEffect, memo, useMemo, useState } from 'react'
import { Box, FormControl, MenuItem, Select } from '@mui/material'
import clsx from 'clsx'
import { CheckIcon } from '@/assets'
import { Optional } from '@/utils'
import Text from '../Text'
import {
  FilterDropdownProps,
  OnChangeFilterDropdownEvent,
  OnChangeFilterDropdownMultiple,
  OnChangeFilterDropdownSingle,
} from './FilterDropdown.types'
import { filterDropdownStyles as styles } from './FilterDropdown.style'
import DropdownIcon from './FilterDropdownIcon'
import DropdownValue from './FilterDropdownValue'
import { filterDropdownClasses } from './FilterDropdown.data'

const FilterDropdown = <Options extends object>({
  options,
  value = '',
  onChange,
  disabled = false,
  idKey,
  valueKey,
  labelKey,
  size = 'small',
  label,
  labelValue,
  enableClear = true,
  fullWidth = false,
  multiple = false,
  ...rest
}: FilterDropdownProps<Options>) => {
  const [open, setOpen] = useState<boolean>(false)
  const [draftValue, setDraftValue] = useState<FilterDropdownProps<Options>['value']>(value)

  // Narrow onChange into two callable shapes so TS knows which signature we're calling.
  const onChangeMultiple = onChange as Optional<OnChangeFilterDropdownMultiple<Options>>
  const onChangeSingle = onChange as Optional<OnChangeFilterDropdownSingle<Options>>

  const handleClear = useCallback(() => {
    const multiValue = [] as Options[] // for multiple select
    const event = {
      target: {
        value: multiple ? multiValue : '',
        name: '',
      },
    } as OnChangeFilterDropdownEvent

    if (multiple) onChangeMultiple?.(multiValue, event)
    else onChangeSingle?.({} as Options, event)
  }, [multiple, onChangeMultiple, onChangeSingle])

  const onSelectChange = useCallback(
    (event: OnChangeFilterDropdownEvent) => {
      const newValue = event.target.value as string | string[]

      setDraftValue(newValue)
      if (Array.isArray(newValue)) {
        const selectedValues = options.filter((option) =>
          newValue.includes(option[valueKey] as string),
        )
        startTransition(() => {
          onChangeMultiple?.(selectedValues as Options[], event)
        })
        return
      }

      const selectedValue = options.find((option) => option[valueKey] === newValue) ?? null
      startTransition(() => {
        onChangeSingle?.(selectedValue as Options, event)
      })
    },
    [onChangeMultiple, onChangeSingle, options, valueKey],
  )

  const selectedValueLabel = useMemo(() => {
    if (multiple && Array.isArray(draftValue) && draftValue.length > 0)
      return `${label?.toString()} (${draftValue.length})`

    return options.find((option) => option[valueKey] === draftValue)?.[labelKey] as string
  }, [label, labelKey, options, multiple, draftValue, valueKey])

  const hasValue = Array.isArray(draftValue) ? draftValue.length > 0 : Boolean(draftValue)

  useEffect(() => {
    setDraftValue(value)
  }, [value])

  return (
    <FormControl disabled={disabled} size={size} fullWidth={fullWidth} sx={styles.formControl}>
      <Select
        value={draftValue}
        displayEmpty
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        className={open || hasValue ? filterDropdownClasses.active : ''}
        onChange={onSelectChange}
        IconComponent={() => (
          <DropdownIcon
            open={open}
            value={draftValue}
            handleClear={handleClear}
            setOpen={setOpen}
            disabled={disabled}
            enableClear={enableClear}
          />
        )}
        renderValue={(selectedValue) => (
          <DropdownValue
            open={open}
            label={label}
            labelValue={labelValue}
            selectedValue={selectedValue}
            selectedValueLabel={selectedValueLabel}
            disabled={disabled}
          />
        )}
        sx={styles.select}
        MenuProps={{
          MenuListProps: {
            sx: styles.menuList,
          },
          PaperProps: {
            sx: styles.dropdownOptions,
          },
        }}
        multiple={multiple}
        {...rest}>
        {options.map((option) => {
          const isSelected = Array.isArray(draftValue)
            ? draftValue.includes(String(option[valueKey]))
            : String(option[valueKey]) === String(draftValue)

          return (
            <MenuItem key={String(option[idKey])} value={option[valueKey] as string | number}>
              <Text
                sx={styles.listItemText}
                className={clsx({
                  [filterDropdownClasses.selectedValue]: isSelected,
                })}>
                {String(option[labelKey])}
              </Text>

              <Box
                sx={styles.checkIcon}
                component={CheckIcon}
                className={clsx({
                  [filterDropdownClasses.selectedValue]: isSelected,
                })}
              />
            </MenuItem>
          )
        })}
      </Select>
    </FormControl>
  )
}

export default memo(FilterDropdown) as unknown as typeof FilterDropdown
